import { LineChart, type ChartDataPoint } from '@zyxantech/axy'
import { Card, PageHeader, Skeleton } from '@zyxantech/tux'
import { ArrowLeft } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useGetTagByIdQuery, useGetTagHistoryQuery } from '@/api/tags.api'
import { ErrorState } from '@/components/shared/ErrorState'
import { ROUTES } from '@/constants/routes'

const RANGES = [
  { id: '1h', label: '1h', ms: 60 * 60 * 1000 },
  { id: '24h', label: '24h', ms: 24 * 60 * 60 * 1000 },
  { id: '7d', label: '7d', ms: 7 * 24 * 60 * 60 * 1000 },
] as const

type RangeId = (typeof RANGES)[number]['id']

export function TagHistoryPage() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [range, setRange] = useState<RangeId>('24h')

  const { data: tag } = useGetTagByIdQuery(id, { skip: !id })

  // Recompute the window when the range changes (kept stable across renders).
  const window = useMemo(() => {
    const now = Date.now()
    const ms = RANGES.find((r) => r.id === range)?.ms ?? RANGES[1].ms
    return { from: new Date(now - ms).toISOString(), to: new Date(now).toISOString() }
  }, [range])

  const { data, isLoading, error, refetch } = useGetTagHistoryQuery(
    { id, query: { from: window.from, to: window.to, limit: 1000 } },
    { skip: !id },
  )

  const chartData: ChartDataPoint[] = useMemo(
    () =>
      (data?.points ?? [])
        .filter((p): p is typeof p & { value: number } => p.value !== null)
        .map((p) => ({ time: new Date(p.timestamp).getTime(), value: p.value })),
    [data],
  )

  return (
    <div className="p-6">
      <button
        type="button"
        onClick={() => navigate(ROUTES.TAGS)}
        className="mb-3 flex items-center gap-1.5 text-xs text-text-muted hover:text-text-secondary"
      >
        <ArrowLeft size={14} /> Tags
      </button>

      <PageHeader
        title={tag?.name ?? 'Tag history'}
        subtitle={tag?.unit ? `History · ${tag.unit}` : 'History'}
        actions={
          <div className="flex gap-1">
            {RANGES.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setRange(r.id)}
                className={`rounded-md px-3 py-1 text-xs ${
                  range === r.id
                    ? 'bg-accent-dim text-accent'
                    : 'bg-bg-overlay text-text-secondary hover:bg-bg-hover'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        }
      />

      <Card padding="md">
        {isLoading ? (
          <Skeleton height={320} />
        ) : error ? (
          <ErrorState error={error} retry={() => void refetch()} />
        ) : (
          <LineChart
            data={chartData}
            series={[{ dataKey: 'value', label: tag?.name ?? 'Value' }]}
            xAxisKey="time"
            height={320}
            showGrid
            showTooltip
            emptyMessage="No history for this range."
          />
        )}
      </Card>
    </div>
  )
}
